import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputField, Modal, Button, NumberInput } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import styles from './SplitCellsModal.module.scss';
import { closeModal } from '_common/modals/ModalsSlice';
import EditorManager from 'Editor/services/EditorManager';
import { Logger } from '_common/services';
import { toast } from 'utils/toast';

const MODAL = 'SplitCellsModal';

const SplitCellsModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const selectedTable: any = useSelector((state) => state.editor.status.selection.TABLE);
  const currentCell = selectedTable?.selectedCells?.[0] || {};

  const [numberOfColumns, setNumberOfColumns] = useState('2');
  const [numberOfRows, setNumberOfRows] = useState('1');
  const [columnFeedback, setColumnFeedback] = useState<string>();
  const [rowFeedback, setRowFeedback] = useState<string>();

  useEffect(() => {
    setColumnFeedback(
      +numberOfColumns < 0
        ? intl.formatMessage({ id: 'NUMBER_OF_COLUMNS_CANNOT_BE_NEGATIVE' })
        : undefined,
    );
  }, [numberOfColumns]);

  useEffect(() => {
    setRowFeedback(
      +numberOfRows < 0
        ? intl.formatMessage({ id: 'NUMBER_OF_ROWS_CANNOT_BE_NEGATIVE' })
        : undefined,
    );
  }, [numberOfRows]);

  useEffect(() => {
    if (currentCell.isMergedCell) {
      setNumberOfColumns(currentCell.colSpan);
      setNumberOfRows(currentCell.rowSpan);
    } else {
      setNumberOfColumns('2');
      setNumberOfRows('1');
    }
  }, [currentCell.isMergedCell]);

  const close = () => {
    dispatch(closeModal('SplitCellsModal'));
    setNumberOfColumns('2');
    setNumberOfRows('1');
  };

  const handleSplitCells = () => {
    try {
      EditorManager.getInstance().splitCells(+numberOfColumns, +numberOfRows);
      toast({
        variant: 'success',
        title: 'SPLIT_CELLS_SUCCESS',
        message: 'SPLIT_CELLS_SUCCESS_MESSAGE',
      });
    } catch (error) {
      Logger.captureException(error);
    } finally {
      close();
    }
  };

  const handleColumnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberOfColumns(e.target.value);
  };

  const handleRowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberOfRows(
      currentCell.isMergedCell && e.target.value > currentCell.rowSpan
        ? currentCell.rowSpan
        : e.target.value,
    );
  };

  return (
    <Modal open={!!isOpen} width="70rem" close={close} testId="split-cells">
      <Modal.Header>
        <FormattedMessage id="editor.menu.table.splitCells" />
      </Modal.Header>
      <Modal.Body>
        <div className={styles.body}>
          <InputField
            label={intl.formatMessage({ id: 'NUMBER_OF_COLUMNS' })}
            size="l"
            testId="number-of-columns-"
            feedbackContent={columnFeedback}
            style={{ width: '30rem' }}
          >
            <NumberInput
              size="l"
              value={numberOfColumns}
              onChange={handleColumnChange}
              placeholder=""
              testId="number-of-columns"
              hasError={!!columnFeedback}
            />
          </InputField>
          <InputField
            label={intl.formatMessage({ id: 'NUMBER_OF_ROWS' })}
            size="l"
            testId="number-of-rows"
            feedbackContent={rowFeedback}
            style={{ width: '30rem' }}
          >
            <NumberInput
              size="l"
              value={numberOfRows}
              onChange={handleRowChange}
              placeholder=""
              testId="number-of-rows"
              hasError={!!rowFeedback}
            />
          </InputField>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="split-cells-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          style={{ margin: '0 0 0 1rem' }}
          onClick={handleSplitCells}
          disabled={!numberOfColumns || !numberOfRows || !!columnFeedback || !!rowFeedback}
          testId="split-cells-submit"
        >
          <FormattedMessage id="editor.menu.table.splitCells" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SplitCellsModal;
